import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Pagination,
  Paper,
  Collapse,
} from "@mui/material";
import { tokens } from "../../../theme";
import logService from "../../../services/SüperAdminServices/logService";
import superAdminService from "../../../services/SüperAdminServices/superAdminService";
import DataTable from "../../../components/SüperAdminComponents/DataTable";
import RollbackOtpModal from "../../../components/RollbackOtpModal";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { format } from "date-fns";

const ENTITY_TYPES = [
  "All",
  "Tenant",
  "Doctor",
  "Patient",
  "Form",
  "FormAnswers",
  "FormInvitation",
  "ScoringFormula",
  "File",
  "PatientFile",
  "DoctorInvitation",
  "PasswordReset",
  "PermissionGroup",
  "DoctorPermissionGroup",
];

const OPERATIONS = ["All", "UPDATE", "DELETE"];

export default function TrashLogsPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Load rolled back IDs from localStorage on mount
  const [rolledBackIds, setRolledBackIds] = useState(() => {
    try {
      const stored = localStorage.getItem('trashLogs_rolledBackIds');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  
  // Save rolled back IDs to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('trashLogs_rolledBackIds', JSON.stringify(Array.from(rolledBackIds)));
    } catch (error) {
      console.error('Error saving rolled back IDs to localStorage:', error);
    }
  }, [rolledBackIds]);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
    totalCount: 0,
    totalPages: 1,
  });

  // Filter state
  const [filters, setFilters] = useState({
    entityType: "All",
    operation: "All",
    onlyActive: false,
    fileName: "",
    entityId: "",
  });

  // Sort state
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");

  useEffect(() => {
    fetchLogs();
  }, [pagination.pageIndex, pagination.pageSize, filters, sortBy, sortOrder]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {
        pageIndex: pagination.pageIndex,
        limit: pagination.pageSize,
        sortBy,
        sortOrder,
        ...(filters.entityType !== "All" && { entityType: filters.entityType }),
        ...(filters.operation !== "All" && { operation: filters.operation }),
        ...(filters.onlyActive && { onlyActive: true }),
        ...(filters.fileName && { fileName: filters.fileName }),
        ...(filters.entityId && { entityId: filters.entityId }),
      };

      const result = await logService.getTrashLogs(params);
      setLogs(result.data);
      setPagination((prev) => ({
        ...prev,
        totalCount: result.pagination.totalCount,
        totalPages: result.pagination.totalPages,
      }));
    } catch (error) {
      console.error("Error fetching trash logs:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error fetching trash logs",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (log) => {
    try {
      const detailedLog = await logService.getTrashLogById(log.id);
      setSelectedLog(detailedLog);
      setViewDialogOpen(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error fetching log details",
        severity: "error",
      });
    }
  };

  const handleRollback = async (log) => {
    setSelectedLog(log);
    setOtpError("");
    setOtpLoading(true);
    
    try {
      // Request OTP first
      await logService.requestRollbackOtp(log.id);
      // Open OTP modal
      setOtpModalOpen(true);
    } catch (error) {
      console.error("Error requesting OTP:", error);
      const errorMessage = error.response?.data?.message || error.message || "Error requesting OTP";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const confirmRollback = async (otp) => {
    if (!selectedLog) return;
    
    try {
      setOtpLoading(true);
      setOtpError("");
      
      const result = await logService.rollbackFromTrash(selectedLog.id, otp);
      
      // Mark this log as rolled back
      setRolledBackIds((prev) => new Set([...prev, selectedLog.id]));
      
      setSnackbar({
        open: true,
        message: result.message || "Rollback completed successfully",
        severity: "success",
      });
      setOtpModalOpen(false);
      setSelectedLog(null);
      
      // Refresh the logs to show updated data
      await fetchLogs();
    } catch (error) {
      console.error("Rollback error:", error);
      const errorMessage = error.response?.data?.message || error.message || "Error performing rollback";
      setOtpError(errorMessage);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to first page
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage - 1 }));
  };

  const handlePageSizeChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageIndex: 0,
    }));
  };

  const resetFilters = () => {
    setFilters({
      entityType: "All",
      operation: "All",
      onlyActive: false,
      fileName: "",
      entityId: "",
    });
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const getFilenameFromHeaders = (headers) => {
    const contentDisposition = headers?.["content-disposition"] || "";
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    return match?.[1] || `trash-logs-export-${Date.now()}.xlsx`;
  };

  const handleExcelExport = async () => {
    try {
      setExportLoading(true);
      const normalizedFilters = {
        ...(filters.entityType !== "All" && { entityType: filters.entityType }),
        ...(filters.operation !== "All" && { operation: filters.operation }),
        ...(filters.onlyActive && { onlyActive: true }),
        ...(filters.fileName && { fileName: filters.fileName }),
        ...(filters.entityId && { entityId: filters.entityId }),
      };
      const response = await superAdminService.exportExcel({
        resource: "trashLogs",
        filters: normalizedFilters,
        sortBy,
        sortOrder,
      });
      const fileName = getFilenameFromHeaders(response.headers);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Excel downloaded successfully", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Excel export failed", severity: "error" });
    } finally {
      setExportLoading(false);
    }
  };

  // Utility functions
  const getRemainingTime = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff <= 0) return { expired: true, text: "Expired" };

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    return {
      expired: false,
      text: `${days}d ${hours}h remaining`,
      days,
      hours,
    };
  };

  const canRollback = (log) => {
    // Check if already rolled back
    if (rolledBackIds.has(log.id)) {
      return false;
    }
    // Check if expired
    const now = new Date();
    const expires = new Date(log.expires_at);
    return expires > now;
  };

  const isRolledBack = (log) => {
    return rolledBackIds.has(log.id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const shortenId = (id) => {
    if (!id) return "-";
    return id.substring(0, 8) + "...";
  };

  const formatJSON = (data) => {
    if (!data) return "{}";
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const columns = [
    {
      id: "entity_type",
      label: "Entity Type",
      format: (value) => (
        <Chip
          label={value}
          size="small"
          sx={{
            backgroundColor: colors.blueAccent[800],
            color: colors.grey[100],
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      id: "entity_id",
      label: "Entity ID",
      format: (value) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {shortenId(value)}
        </Typography>
      ),
    },
    {
      id: "operation",
      label: "Operation",
      format: (value) => (
        <Chip
          label={value}
          size="small"
          sx={{
            backgroundColor:
              value === "DELETE" ? colors.redAccent[800] : colors.blueAccent[700],
            color: colors.grey[100],
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      id: "performed_by_name",
      label: "Performed By",
      format: (value, row) => (
        <Box>
          <Typography variant="body2">{value || "-"}</Typography>
          {row.performed_by_role && (
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              {row.performed_by_role}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: "created_at",
      label: "Created At",
      format: (value) => formatDate(value),
    },
    {
      id: "expires_at",
      label: "Status",
      format: (value, row) => {
        const remaining = getRemainingTime(value);
        return (
          <Chip
            label={remaining.text}
            size="small"
            sx={{
              backgroundColor: remaining.expired
                ? colors.grey[600]
                : colors.greenAccent[800],
              color: colors.grey[100],
              fontWeight: 600,
            }}
          />
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        p: 3,
        width: "100%",
        minWidth: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          width: "100%",
          minWidth: "100%",
        }}
      >
        <DeleteIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[200]} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Trash Logs Management
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            endIcon={filtersOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            onClick={() => setFiltersOpen((prev) => !prev)}
            sx={{
              color: colors.grey[100],
              backgroundColor: filtersOpen ? colors.blueAccent[700] : colors.primary[300],
              "&:hover": { backgroundColor: filtersOpen ? colors.blueAccent[800] : colors.primary[200] },
            }}
          >
            Filters
          </Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExcelExport} disabled={exportLoading}>
            {exportLoading ? "Exporting..." : "Export Excel"}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Collapse in={filtersOpen} timeout={250} unmountOnExit>
      <Paper
        sx={{
          p: 2,
          mb: 3,
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          border: `1px solid ${colors.grey[800]}30`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <FilterListIcon sx={{ color: colors.grey[100] }} />
          <Typography variant="h6" sx={{ color: colors.grey[100] }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: colors.grey[300] }}>Entity Type</InputLabel>
              <Select
                value={filters.entityType}
                onChange={(e) => handleFilterChange("entityType", e.target.value)}
                label="Entity Type"
                sx={{
                  color: colors.grey[100],
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.grey[700],
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.grey[600],
                  },
                  "& .MuiSvgIcon-root": {
                    color: colors.grey[300],
                  },
                }}
              >
                {ENTITY_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: colors.grey[300] }}>Operation</InputLabel>
              <Select
                value={filters.operation}
                onChange={(e) => handleFilterChange("operation", e.target.value)}
                label="Operation"
                sx={{
                  color: colors.grey[100],
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.grey[700],
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.grey[600],
                  },
                  "& .MuiSvgIcon-root": {
                    color: colors.grey[300],
                  },
                }}
              >
                {OPERATIONS.map((op) => (
                  <MenuItem key={op} value={op}>
                    {op}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.onlyActive}
                  onChange={(e) => handleFilterChange("onlyActive", e.target.checked)}
                  sx={{
                    color: colors.grey[300],
                    "&.Mui-checked": {
                      color: colors.greenAccent[500],
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: colors.grey[200] }}>Only Active</Typography>
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="File Name"
              value={filters.fileName}
              onChange={(e) => handleFilterChange("fileName", e.target.value)}
              placeholder="Search by file name..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: colors.grey[100],
                  "& fieldset": {
                    borderColor: colors.grey[700],
                  },
                  "&:hover fieldset": {
                    borderColor: colors.grey[600],
                  },
                },
                "& .MuiInputLabel-root": {
                  color: colors.grey[300],
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={resetFilters}
                sx={{
                  color: colors.grey[200],
                  borderColor: colors.grey[700],
                  "&:hover": {
                    borderColor: colors.grey[600],
                    backgroundColor: `${colors.grey[800]}30`,
                  },
                }}
              >
                Reset
              </Button>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={fetchLogs}
                  sx={{
                    color: colors.grey[200],
                    "&:hover": {
                      backgroundColor: `${colors.grey[800]}30`,
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      </Collapse>

      {/* DataTable with custom actions */}
      <Box sx={{ mb: 2 }}>
        <DataTable
          columns={columns}
          data={logs}
          onView={handleView}
          loading={loading}
          emptyMessage="No trash logs found"
      customActions={(row) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, alignItems: "center" }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleView(row)}
              sx={{
                color: colors.blueAccent[400],
                "&:hover": {
                  backgroundColor: `${colors.blueAccent[800]}30`,
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isRolledBack(row) ? (
            <Tooltip title="This item has been rolled back">
              <Chip
                label="Rolled Back"
                size="small"
                sx={{
                  backgroundColor: colors.greenAccent[800],
                  color: colors.grey[100],
                  fontWeight: 600,
                  height: "28px",
                  fontSize: "0.75rem",
                }}
              />
            </Tooltip>
          ) : canRollback(row) ? (
            <Tooltip title="Rollback">
              <IconButton
                size="small"
                onClick={() => handleRollback(row)}
                sx={{
                  color: colors.greenAccent[400],
                  "&:hover": {
                    backgroundColor: `${colors.greenAccent[800]}30`,
                  },
                }}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      )}
        />
      </Box>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            p: 2,
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            borderRadius: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ color: colors.grey[200] }}>
              Showing {pagination.pageIndex * pagination.pageSize + 1} to{" "}
              {Math.min(
                (pagination.pageIndex + 1) * pagination.pageSize,
                pagination.totalCount
              )}{" "}
              of {pagination.totalCount} entries
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                sx={{
                  color: colors.grey[100],
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.grey[700],
                  },
                  "& .MuiSvgIcon-root": {
                    color: colors.grey[300],
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={pagination.totalPages}
            page={pagination.pageIndex + 1}
            onChange={handlePageChange}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                color: colors.grey[200],
                "&.Mui-selected": {
                  backgroundColor: colors.blueAccent[600],
                  color: colors.grey[100],
                },
              },
            }}
          />
        </Box>
      )}

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.grey[100] }}>Trash Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Entity Type
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedLog.entity_type}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Entity ID
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: colors.grey[100], fontFamily: "monospace" }}
                >
                  {selectedLog.entity_id}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Operation
                </Typography>
                <Chip
                  label={selectedLog.operation}
                  size="small"
                  sx={{
                    backgroundColor:
                      selectedLog.operation === "DELETE"
                        ? colors.redAccent[800]
                        : colors.blueAccent[700],
                    color: colors.grey[100],
                    fontWeight: 600,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Performed By
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedLog.performed_by_name || "-"} (
                  {selectedLog.performed_by_role || "-"})
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Created At
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {formatDate(selectedLog.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Expires At
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {formatDate(selectedLog.expires_at)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Status
                </Typography>
                {(() => {
                  const remaining = getRemainingTime(selectedLog.expires_at);
                  return (
                    <Chip
                      label={remaining.text}
                      size="small"
                      sx={{
                        backgroundColor: remaining.expired
                          ? colors.grey[600]
                          : colors.greenAccent[800],
                        color: colors.grey[100],
                        fontWeight: 600,
                      }}
                    />
                  );
                })()}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 1 }}>
                  Data (JSON)
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: colors.primary[600],
                    maxHeight: "400px",
                    overflow: "auto",
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      color: colors.grey[100],
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                    }}
                  >
                    {formatJSON(selectedLog.data)}
                  </pre>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedLog && isRolledBack(selectedLog) ? (
            <Chip
              label="Rolled Back"
              sx={{
                backgroundColor: colors.greenAccent[800],
                color: colors.grey[100],
                fontWeight: 600,
                mr: 2,
              }}
            />
          ) : selectedLog && canRollback(selectedLog) ? (
            <Button
              onClick={() => {
                setViewDialogOpen(false);
                handleRollback(selectedLog);
              }}
              startIcon={<RestoreIcon />}
              sx={{
                color: colors.greenAccent[400],
                "&:hover": {
                  backgroundColor: `${colors.greenAccent[800]}30`,
                },
              }}
            >
              Rollback
            </Button>
          ) : null}
          <Button
            onClick={() => setViewDialogOpen(false)}
            sx={{ color: colors.grey[100] }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rollback OTP Modal */}
      <RollbackOtpModal
        open={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          setOtpError("");
          setSelectedLog(null);
        }}
        onConfirm={confirmRollback}
        loading={otpLoading}
        error={otpError}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

